import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { FormControl } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { isValid } from 'date-fns';
import React from 'react';
import { getTimeValueFromDate } from '../DateTimePicker';
import { formatTime } from '../TimePicker';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { type } from './types';
const oneSecondInMs = 1000;
const oneMinuteInMs = 60 * oneSecondInMs;
const oneHourInMs = 60 * oneMinuteInMs;
const WidgetFormTimePickerV2 = (props) => {
    var _a, _b, _c;
    const data = props.getDataNumberOrUndefined();
    let value = undefined;
    if (isValid(data)) {
        const date = new Date(0);
        date.setHours(Math.floor(data / oneHourInMs));
        date.setMinutes(Math.floor((data % oneHourInMs) / oneMinuteInMs));
        value = date;
    }
    const error = getVisibleError(props);
    const formLabelSuffix = getFormLabelSuffix(props);
    const label = formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label;
    const readOnly = props.config.readOnly || ((_a = props.rootParams.formParams) === null || _a === void 0 ? void 0 : _a.readOnly);
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, required: (_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.required, error: !!error, disabled: props.config.disabled },
        React.createElement(TimePicker, { label: label, readOnly: readOnly, defaultValue: value, ampm: props.config.useAmPm, timeSteps: { minutes: (_c = props.config.timeStepMinutes) !== null && _c !== void 0 ? _c : 5 }, slotProps: {
                textField: {
                    error: !!error,
                },
            }, onChange: (newValue) => {
                if (readOnly) {
                    return;
                }
                if (!newValue) {
                    props.setDataNumberOrUndefined(undefined);
                }
                else {
                    props.setDataNumberOrUndefined(getTimeValueFromDate(newValue));
                }
                renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
            } }),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormTimePickerEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'Time Picker V2: ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            description: true,
            autoFocus: true,
            children: [
                {
                    type: 'form.checkbox',
                    key: 'useAmPm',
                    label: 'Use AM/PM',
                    defaultValue: true,
                },
                {
                    type: 'form.select',
                    key: 'timeStepMinutes',
                    label: 'Minute Steps',
                    defaultValue: 5,
                    tooltip: {
                        text: 'The number of minutes between each time step.',
                    },
                    values: [
                        { value: 1, label: '1 Minute' },
                        { value: 5, label: '5 Minute' },
                        { value: 10, label: '10 Minute' },
                        { value: 15, label: '15 Minute' },
                        { value: 30, label: '30 Minute' },
                        { value: 60, label: '60 Minute' },
                    ],
                },
            ],
        }), buildValidateTab(props, {
            required: true,
        }), buildHiddenTab(props), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormTimePickerV2, Object.assign({}, props))));
};
export const widgetFormTimePickerV2 = {
    type,
    validate: (props) => {
        var _a;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getDataNumberOrUndefined();
        if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) && value == null) {
            if (props.config.validate.requiredMessage) {
                return props.config.validate.requiredMessage;
            }
            else if (props.config.label) {
                return props.config.label + ' is required';
            }
            else {
                return 'Required';
            }
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        const value = props.getDataNumberOrUndefined();
        if (value != null) {
            return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), formatTime(value, props.config.useAmPm ? '12h' : '24h'));
        }
        return [];
    },
    component: WidgetFormTimePickerV2,
    icon: React.createElement(CalendarMonthIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormTimePickerEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'timePickerV2', label: 'Time Picker V2' }),
};
